import React from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { isAuthorized } from "../../custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "../../utils/constants/authorization-constants";
import { useAppSelector } from "../../custom-hooks/redux/redux";
import { NotAuthorizedPage } from "../../reusable-components/NotAuthorizedPage/NotAuthorizedPage";
import { GridColumns } from "@mui/x-data-grid";
import { PayrollInputDTO } from "../../models/payroll-inputs";
import { ClientManagerPayrollInputsApiService } from "../../services/payroll-inputs";
import { Typography } from "@mui/material";
import { intlFormatDateShort } from "../../utils/time-utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import Table from "../../reusable-components/Table/Table";
import { FT_PPI } from "../../utils/constants/filter-tables-constants";
import AttachedDocuments from "../AttachedDocuments/AttachedDocuments";
import { LegalEntityQuickfilter, useLegalEntityQuickFilter } from "../Quickfilters/LegalEntityQuickfilter";
import QuickfiltersContainer from "../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import AddEditClientManagerPayrollInput from "../AddEditClientManagerPayrollInput/AddEditClientManagerPayrollInput";
const ProvidePayrollInputTable = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const tRP = "components.provide_payroll_inputs_table";

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const hasAccess = isAuthorized([AUTHORIZATIONS.EmployeesAccess.ProvidePayrollInput], currentUserAuthorizations);

  const {
    legalEntitiesQuickFilter,
    setLegalEntitiesQuickFilter,
    selectedLegalEntity,
    legalEntityQFIsLoading,
    allLegalEntities,
  } = useLegalEntityQuickFilter(true, undefined, [AUTHORIZATIONS.EmployeesAccess.ProvidePayrollInput]);

  const columns: GridColumns<PayrollInputDTO> = [
    {
      field: "number",
      valueGetter: ({ row }) => row.number,
      headerName: "#",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
    },
    {
      field: "legalEntity",
      flex: 1,
      headerAlign: "center",
      headerName: t(`common_words.legal_entity`),
      valueGetter: ({ row }) => row.legalEntity?.code ?? row.legalEntity?.name,
    },
    {
      field: "createdBy",
      flex: 1,
      headerAlign: "center",
      headerName: t(`${tRP}.fields.requested_by`),
      renderCell: ({ row }) => (
        <Typography>
          {row.createdByUser?.name}
          <br /> <Typography variant="body2">on {intlFormatDateShort(row.createdAt)}</Typography>
        </Typography>
      ),
    },
    {
      field: "employee",
      flex: 1,
      headerAlign: "center",
      headerName: t(`common_words.employee`),
    },
    {
      field: "description",
      flex: 2,
      headerAlign: "center",
      headerName: t(`common_words.description`),
    },
    {
      field: "inputType",
      flex: 1,
      headerAlign: "center",
      headerName: t(`${tRP}.fields.type`),
      valueGetter: ({ row }) => row.inputType?.name,
    },
    {
      field: "payrollInputDocuments",
      flex: 0.7,
      headerAlign: "center",
      align: "center",
      headerName: t(`${tRP}.fields.documents`),
      renderCell: ({ row }) =>
        row.payrollInputDocuments.length > 0 && (
          <AttachedDocuments
            buttonSize="2x"
            documentIDs={row.payrollInputDocuments.map(({ document }) => document?.id!)}
            count={row.payrollInputDocuments.length}
            documentIsReadOnly
            useSimpleView
            documentsNames={row?.payrollInputDocuments?.map((el) => el?.document?.name ?? "")}
          />
        ),
    },
    {
      field: "status",
      flex: 0.5,
      headerAlign: "center",
      headerName: t(`${tRP}.fields.status`),
      renderCell: ({ row }) => (
        <Typography>
          {row.status?.name}
          {row.isVerified && (
            <FontAwesomeIcon
              color={theme.palette.success.main}
              icon={faCircleCheck}
            />
          )}
        </Typography>
      ),
    },
  ];

  return (
    <>
      {hasAccess ? (
        <>
          <Table
            translationRootPath={tRP}
            serviceApi={new ClientManagerPayrollInputsApiService()}
            columns={columns}
            filter={FT_PPI}
            quickFilters={
              <QuickfiltersContainer>
                <LegalEntityQuickfilter
                  quickFilters={legalEntitiesQuickFilter}
                  handleChangeQuickFilters={setLegalEntitiesQuickFilter}
                  allLegalEntitiesOption
                  allLegalEntities={allLegalEntities}
                />
              </QuickfiltersContainer>
            }
            CustomAddEditComponent={AddEditClientManagerPayrollInput}
            propsForCustomAddEdit={{ defaultLegalEntity: selectedLegalEntity }}
            addEditType="custom"
            hideSelectionColumn
            hideRemoveIcon
          />
        </>
      ) : (
        <NotAuthorizedPage />
      )}
    </>
  );
};
export default ProvidePayrollInputTable;
